import React, { useContext, useEffect, useRef, useState } from "react";
import { FiRefreshCw, FiZoomIn, FiZoomOut } from "react-icons/fi";
import { _Api } from "../../api/_call";
import { dataListExample } from "components/common/barcodeTemplate/dataListExample";
import { template1 } from "../../../components/common/barcodeTemplate/template1";
import {
    DEFAULT_CUSTOM_TEMPLATE_CONFIG,
    hasCustomTemplateConfig,
    mergeCustomTemplateConfig,
    parseCustomTemplateConfig,
} from "../../../components/common/barcodeTemplate/customTemplateConfig";
import {
    CustomTemplatePreviewEditor,
    customTemplateConfig as defaultCustomTemplateConfig,
} from "./customTemplate/CustomTemplatePrintout";
import barcodePrintOutContext from "../context/BarcodePrintOutContext.js";
import { Document, Page, pdfjs } from "react-pdf";
import { encodePriceWithCode } from "components/common/helpers/barcodeHelper";
import { formatWeightValue } from "../../../components/common/barcodeTemplate/printQuantity";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const PREVIEW_MIN_ZOOM = 50;
const PREVIEW_MAX_ZOOM = 300;
const PREVIEW_STEP = 25;
const LABEL_WIDTH = 75;
const LABEL_HEIGHT = 14;
const PREVIEW_TEXT_OFFSET = -1.75;
const DEFAULT_TEXT_FONT_SIZE = 6;

const FIELD_LABELS = {
    barcode: "Barcode",
    qr: "QR code",
    item: "Item",
    collection: "Collection",
    item_size: "Item size",
    metal: "Metal",
    weight: "Weight",
    stone_group: "Stone group",
    stone_name: "Stone name",
    shape: "Shape",
    cut: "Cut",
    color: "Color",
    clarity: "Clarity",
    stone_size: "Stone size",
    stone_pcs: "Stone pcs",
    stone_weight: "Stone weight",
    price: "Price",
    text: "Text",
    image: "Image",
};

const getSelectedTemplateValue = (selectedTemplate) => {
    if (!selectedTemplate) return "";
    if (typeof selectedTemplate === "string") return selectedTemplate;
    return selectedTemplate.value || "";
};

const PREVIEW_FIELD_SIZES = {
    sku: { width: 24, height: 1.8 },
    barcode: { width: 20, height: 4 },
    stock_id: { width: 12, height: 1.8 },
    collection: { width: 24, height: 1.8 },
    item: { width: 12, height: 1.8 },
    item_size: { width: 8, height: 1.8 },
    metal: { width: 14, height: 1.8 },
    weight: { width: 14, height: 1.8 },
    price: { width: 24, height: 1.8 },
    text: { width: 18, height: 1.8 },
    image: { width: 8, height: 8 },
    stone_data: { width: 19, height: 8 },
};

const PREVIEW_FIELD_POSITIONS = {
    sku: { x: 31.5, y: 2.5 },
    barcode: { x: 31.5, y: 3.3 },
    item: { x: 31.5, y: 9.8 },
    collection: { x: 31.5, y: 3.2 },
    item_size: { x: 44.5, y: 9.8 },
    metal: { x: 44.5, y: 9.8 },
    weight: { x: 45.5, y: 12 },
    price: { x: 31.5, y: 12 },
    text: { x: 45.5, y: 12 },
    image: { x: 1, y: 1 },
    stone_data: { x: 55, y: 0 },
};

const getPreviewFieldText = (fieldName, configInfo = {}) => {
    const item = dataListExample[0] || {};
    const stone = item.Stones_Data?.[0] || {};
    const textByField = {
        sku: item.SKU,
        item: item.stock_id,
        collection: item.collection_code || item.collection_name,
        item_size: item.size_name,
        metal: item.metal_code,
        weight: `${formatWeightValue(item.NetWt || item.GrossWt)}g`,
        price: configInfo.price_display === "with_code"
            ? encodePriceWithCode("31,800.00", configInfo.with_code || {})
            : "31,800.00",
        text: "Text",
        stone_name: stone.stone_details?.code,
        stone_group: stone.stone_group_details?.code,
        clarity: stone.stone_clarity_details?.code,
        stone_size: stone.stone_size_details?.code || stone.size_name,
        shape: stone.stone_shape_details?.code,
        color: stone.stone_color_details?.code,
        cut: stone.stone_cut_details?.code,
        stone_pcs: stone.Pcs,
        stone_weight: `${stone.Cts || ""}${stone.Unit ? ` ${stone.Unit.substring(0, 1).toLowerCase()}` : ""}`,
    };

    return textByField[fieldName] || "";
};

const getStoneLines = (selectedFields, requestedCount = 5) => {
    const stones = dataListExample[0]?.Stones_Data || [];
    const count = Math.min(
        MAX_PREVIEW_STONE_COUNT,
        Math.max(1, Math.floor(Number(requestedCount) || 1))
    );

    return Array.from({ length: count }, (_, index) => (
        stones.length > 0 ? stones[index % stones.length] : {}
    )).map((stone) => {
        const parts = [];
        if (selectedFields.has("stone_name") && stone.stone_details?.code) {
            parts.push(stone.stone_details.code);
        }
        if (selectedFields.has("stone_group") && stone.stone_group_details?.code) {
            parts.push(stone.stone_group_details.code);
        }
        if (selectedFields.has("clarity") && stone.stone_clarity_details?.code) {
            parts.push(stone.stone_clarity_details.code);
        }
        if (selectedFields.has("shape") && stone.stone_shape_details?.code) {
            parts.push(stone.stone_shape_details.code);
        }
        if (selectedFields.has("color") && stone.stone_color_details?.code) {
            parts.push(stone.stone_color_details.code);
        }
        if (selectedFields.has("cut") && stone.stone_cut_details?.code) {
            parts.push(stone.stone_cut_details.code);
        }
        if (selectedFields.has("stone_size") && (stone.stone_size_details?.code || stone.size_name)) {
            parts.push(stone.stone_size_details?.code || stone.size_name);
        }
        if (selectedFields.has("stone_pcs") && stone.Pcs) {
            parts.push(String(stone.Pcs));
        }
        if (selectedFields.has("stone_weight") && stone.Cts) {
            parts.push(`${stone.Cts}${stone.Unit ? ` ${stone.Unit.substring(0, 1).toLowerCase()}` : ""}`);
        }
        return parts.join(" ");
    });
};

const MAX_PREVIEW_STONE_COUNT = 5;
const DEFAULT_PREVIEW_LINE_SPACING = 2.5;
const DEFAULT_PREVIEW_STONE_COUNT = Math.min(
    2,
    MAX_PREVIEW_STONE_COUNT
);
const STONE_FIELD_NAMES = [
    "stone_name",
    "stone_group",
    "clarity",
    "stone_size",
    "shape",
    "color",
    "cut",
    "stone_pcs",
    "stone_weight",
];

const BarcodeImagePreviewObject = ({ imageConfig, scale, onChange }) => {
    const dragRef = useRef(null);
    const [draft, setDraft] = useState(null);
    const current = draft || imageConfig;
    if (!current?.data) return null;

    const position = current.position || { x: 1, y: 1 };
    const size = current.size || { width: 8, height: 8 };
    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

    const start = (event, mode = "move", corner = "") => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        dragRef.current = {
            mode,
            corner,
            startClientX: event.clientX,
            startClientY: event.clientY,
            position: { ...position },
            size: { ...size },
        };
    };

    const move = (event) => {
        const drag = dragRef.current;
        if (!drag) return;
        const dx = (event.clientX - drag.startClientX) / scale;
        const dy = (event.clientY - drag.startClientY) / scale;
        const nextPosition = { ...drag.position };
        const nextSize = { ...drag.size };

        if (drag.mode === "move") {
            nextPosition.x = clamp(drag.position.x + dx, 0, LABEL_WIDTH - nextSize.width);
            nextPosition.y = clamp(drag.position.y + dy, 0, LABEL_HEIGHT - nextSize.height);
        } else {
            const minSize = 1;
            if (drag.corner.includes("e")) nextSize.width = clamp(drag.size.width + dx, minSize, LABEL_WIDTH);
            if (drag.corner.includes("s")) nextSize.height = clamp(drag.size.height + dy, minSize, LABEL_HEIGHT);
            if (drag.corner.includes("w")) {
                const right = drag.position.x + drag.size.width;
                nextPosition.x = clamp(drag.position.x + dx, 0, right - minSize);
                nextSize.width = right - nextPosition.x;
            }
            if (drag.corner.includes("n")) {
                const bottom = drag.position.y + drag.size.height;
                nextPosition.y = clamp(drag.position.y + dy, 0, bottom - minSize);
                nextSize.height = bottom - nextPosition.y;
            }
        }

        setDraft({
            ...current,
            position: {
                x: Math.round(nextPosition.x * 100) / 100,
                y: Math.round(nextPosition.y * 100) / 100,
            },
            size: {
                width: Math.round(nextSize.width * 100) / 100,
                height: Math.round(nextSize.height * 100) / 100,
            },
        });
    };

    const finish = () => {
        if (dragRef.current && draft) onChange(draft);
        dragRef.current = null;
        setDraft(null);
    };

    return (
        <div
            className="barcode_profile_preview_image_drag_field"
            style={{
                left: `${position.x * scale}px`,
                top: `${position.y * scale}px`,
                width: `${size.width * scale}px`,
                height: `${size.height * scale}px`,
            }}
            onPointerDown={(event) => start(event)}
            onPointerMove={move}
            onPointerUp={finish}
            onPointerCancel={finish}
        >
            <img src={current.data} alt="Label" draggable="false" />
            {["nw", "n", "ne", "sw", "s", "se"].map((corner) => (
                <span
                    key={corner}
                    className={`barcode_profile_preview_image_resize_handle is-${corner}`}
                    onPointerDown={(event) => start(event, "resize", corner)}
                />
            ))}
        </div>
    );
};

const getCustomPreviewConfig = (selectedFields, configInfo = {}, previewStoneCount) => ({
    barcode: selectedFields.has("barcode"),
    stock_id: selectedFields.has("item"),
    collection: selectedFields.has("collection"),
    metal: selectedFields.has("metal"),
    weight: selectedFields.has("weight"),
    pcs: selectedFields.has("pcs"),
    weight_display: configInfo.weight_display || "nwt",
    price: selectedFields.has("price"),
    price_display: configInfo.price_display || "retail_price",
    text: selectedFields.has("text"),
    text_message: configInfo.text_message || "Text",
    text_font_size: Number(configInfo.text_font_size) || DEFAULT_TEXT_FONT_SIZE,
    with_code: configInfo.with_code || {},
    image: selectedFields.has("image"),
    image_config: configInfo.image_config || null,
    stone_name: selectedFields.has("stone_name"),
    stone_group: selectedFields.has("stone_group"),
    clarity: selectedFields.has("clarity"),
    stone_size: selectedFields.has("stone_size"),
    shape: selectedFields.has("shape"),
    color: selectedFields.has("color"),
    cut: selectedFields.has("cut"),
    stone_pcs: selectedFields.has("stone_pcs"),
    stone_weight: selectedFields.has("stone_weight"),
    preview_stone_count: previewStoneCount,
});

const clonePreviewTemplateConfig = (value) => {
    const storedConfig = parseCustomTemplateConfig(value) || defaultCustomTemplateConfig;
    return JSON.parse(JSON.stringify(
        mergeCustomTemplateConfig(DEFAULT_CUSTOM_TEMPLATE_CONFIG, storedConfig)
    ));
};

const BarcodeConfigPreview = ({ checkConfigData, resetKey = 0 }) => {
    const { setCheckConfigData } = useContext(barcodePrintOutContext) || {};
    const [zoom, setZoom] = useState(100);
    const [pdfUri, setPdfUri] = useState(null);
    const previewRef = useRef(null);
    const dragRef = useRef(null);
    const [dragPositions, setDragPositions] = useState({});
    const [settledFields, setSettledFields] = useState({});
    const skipNextPreviewRenderRef = useRef(false);
    // Barcode image depends only on the sample value/type, so edits to other fields reuse it.
    const barcodeRequestCacheRef = useRef(new Map());
    const storedPreviewConfig = checkConfigData?.custom_template_config_info
        || checkConfigData?.custom_template_config;
    const [customPreviewConfig, setCustomPreviewConfig] = useState(() => (
        clonePreviewTemplateConfig(storedPreviewConfig)
    ));
    const customPreviewConfigRef = useRef(customPreviewConfig);
    const [hasCustomPreviewAdjustments, setHasCustomPreviewAdjustments] = useState(
        hasCustomTemplateConfig(storedPreviewConfig)
    );
    const [previewStoneCount, setPreviewStoneCount] = useState(DEFAULT_PREVIEW_STONE_COUNT);
    const [previewLineSpacing, setPreviewLineSpacing] = useState(DEFAULT_PREVIEW_LINE_SPACING);
    const [previewLayerFields, setPreviewLayerFields] = useState(["sku"]);
    const previousStoneFieldsRef = useRef([]);
    const selectedFields = new Set(checkConfigData?.config_check_list || []);
    const stoneListVisible = STONE_FIELD_NAMES.some((fieldName) => selectedFields.has(fieldName));
    const templateValue = getSelectedTemplateValue(checkConfigData?.current_template_layout_selected);
    const stoneLines = getStoneLines(selectedFields, previewStoneCount);
    const selectedFieldKey = [...selectedFields].sort().join("|");
    const configInfo = checkConfigData?.config_check_info || {};
    const imageConfigSignature = JSON.stringify(configInfo.image_config || null);
    const profileId = configInfo._id || "new-profile";
    const zoomScale = zoom / 100;
    const previewWidth = 320 * zoomScale;
    const scale = previewWidth / LABEL_WIDTH;
    const previewHeight = ((templateValue === "template_1" ? 13 : LABEL_HEIGHT) / LABEL_WIDTH) * previewWidth;
    const customPreviewConfigSignature = JSON.stringify(customPreviewConfig);
    const activePreviewFields = [...new Set([
        ...[...selectedFields].map((fieldName) => fieldName === "item" ? "stock_id" : fieldName),
        ...(templateValue === "template_1" ? ["stock_id"] : []),
        ...previewLayerFields,
    ])];

    const commitCustomPreviewConfig = (updater) => {
        const nextConfig = typeof updater === "function"
            ? updater(customPreviewConfigRef.current)
            : updater;
        customPreviewConfigRef.current = nextConfig;
        setCustomPreviewConfig(nextConfig);
        setHasCustomPreviewAdjustments(true);
        setCheckConfigData?.((currentConfig) => ({
            ...currentConfig,
            custom_template_config: nextConfig,
            custom_template_config_info: nextConfig,
        }));
    };

    useEffect(() => {
        if (skipNextPreviewRenderRef.current) {
            skipNextPreviewRenderRef.current = false;
            return undefined;
        }

        let isCurrentRequest = true;

        if (templateValue !== "template_1") {
            setPdfUri(null);
            return () => {
                isCurrentRequest = false;
            };
        }

        const previewConfig = getCustomPreviewConfig(selectedFields, configInfo, previewStoneCount);
        const printConfig = {
            ...configInfo,
            ...previewConfig,
            stock_id: true,
            preview_only: true,
            ...(hasCustomPreviewAdjustments
                ? { custom_template_config_info: customPreviewConfig }
                : {}),
        };

        const previewItem = dataListExample[0] || {};
        const sampleStones = previewItem.Stones_Data || [];
        const requestedPreviewStoneCount = Math.min(
            MAX_PREVIEW_STONE_COUNT,
            Math.max(1, Math.floor(Number(previewStoneCount) || 1))
        );
        const previewDataList = [{
            ...previewItem,
            Stones_Data: Array.from(
                { length: requestedPreviewStoneCount },
                (_, index) => ({
                    ...(sampleStones.length > 0
                        ? sampleStones[index % sampleStones.length]
                        : {}),
                    preview_index: index,
                })
            ),
        }];
        const barcodeDisplay = configInfo.barcode_display === "sku" ? "sku" : "stock_id";
        const barcodeValue = barcodeDisplay === "sku"
            ? (previewItem.SKU || "SB03218KYGDM-45")
            : (previewItem.stock_id || "100");
        const barcodeRequest = barcodeDisplay === "sku"
            ? { barcodedata: [barcodeValue], dataType: "sku" }
            : { barcodedata: [barcodeValue] };

        const barcodeCache = barcodeRequestCacheRef.current;
        const barcodeCacheKey = JSON.stringify(barcodeRequest);
        if (!barcodeCache.has(barcodeCacheKey)) {
            barcodeCache.set(
                barcodeCacheKey,
                Promise.resolve(_Api(barcodeRequest, "api/v1/barcode/create")).catch((err) => {
                    barcodeCache.delete(barcodeCacheKey); // let the next edit retry
                    throw err;
                })
            );
        }

        barcodeCache.get(barcodeCacheKey)
            .then((barcodeResponse) => template1(
                barcodeResponse,
                previewDataList,
                printConfig,
                "uri"
            ))
            .then((uri) => {
                if (isCurrentRequest) setPdfUri(uri);
            })
            .catch(() => {
                if (isCurrentRequest) console.error("Unable to refresh barcode profile preview");
            });

        return () => {
            isCurrentRequest = false;
        };
    }, [
        configInfo.price_display,
        JSON.stringify(configInfo.with_code || {}),
        JSON.stringify(configInfo.normal_encode_pre_suf_fix || {}),
        configInfo.weight_display,
        configInfo.barcode_display,
        configInfo.text_message,
        configInfo.text_font_size,
        selectedFieldKey,
        templateValue,
        customPreviewConfigSignature,
        imageConfigSignature,
        hasCustomPreviewAdjustments,
        previewStoneCount,
    ]);

    const changeZoom = (delta) => {
        setZoom((currentZoom) => Math.min(
            PREVIEW_MAX_ZOOM,
            Math.max(PREVIEW_MIN_ZOOM, currentZoom + delta)
        ));
    };

    useEffect(() => {
        const preview = previewRef.current;
        if (!preview) return undefined;

        const handleWheel = (event) => {
            if (!event.ctrlKey) return;
            event.preventDefault();
            event.stopPropagation();
            changeZoom(event.deltaY < 0 ? PREVIEW_STEP : -PREVIEW_STEP);
        };

        preview.addEventListener("wheel", handleWheel, { passive: false });
        return () => preview.removeEventListener("wheel", handleWheel);
    }, []);

    useEffect(() => {
        setDragPositions({});
        setSettledFields({});
    }, [pdfUri, selectedFieldKey, templateValue]);

    useEffect(() => {
        const nextConfig = clonePreviewTemplateConfig(storedPreviewConfig);
        customPreviewConfigRef.current = nextConfig;
        setCustomPreviewConfig(nextConfig);
        setHasCustomPreviewAdjustments(hasCustomTemplateConfig(storedPreviewConfig));
        setPreviewLayerFields(["sku"]);
        setPreviewStoneCount(DEFAULT_PREVIEW_STONE_COUNT);
        setPreviewLineSpacing(DEFAULT_PREVIEW_LINE_SPACING);
    }, [templateValue, resetKey, profileId]);

    useEffect(() => {
        const savedCount = Number(configInfo.preview_stone_count);
        if (Number.isFinite(savedCount) && savedCount > 0) {
            setPreviewStoneCount(Math.min(MAX_PREVIEW_STONE_COUNT, Math.max(1, Math.floor(savedCount))));
        }
    }, [configInfo.preview_stone_count]);

    useEffect(() => {
        previousStoneFieldsRef.current = [];
    }, [profileId, templateValue]);

    const handleCustomPreviewPositionChange = (fieldName, position, options = {}) => {
        if (options.skipPreviewRender) {
            skipNextPreviewRenderRef.current = true;
        }

        commitCustomPreviewConfig((currentConfig) => {
            const nextConfig = {
                ...currentConfig,
                stone_data: { ...currentConfig.stone_data },
            };

            if (options.stoneRowDelta) {
                Object.keys(nextConfig.stone_data || {}).forEach((stoneFieldName) => {
                    if (stoneFieldName === "line_space_offset") return;
                    const currentPosition = nextConfig.stone_data[stoneFieldName]?.position;
                    if (!currentPosition) return;

                    nextConfig.stone_data[stoneFieldName] = {
                        ...nextConfig.stone_data[stoneFieldName],
                        position: {
                            x: parseFloat(currentPosition.x) + options.stoneRowDelta.x,
                            y: parseFloat(currentPosition.y) + options.stoneRowDelta.y,
                        },
                    };
                });
            } else if (nextConfig.stone_data?.[fieldName]?.position) {
                nextConfig.stone_data[fieldName] = {
                    ...nextConfig.stone_data[fieldName],
                    position: {
                        ...nextConfig.stone_data[fieldName].position,
                        ...position,
                    },
                };
            } else if (nextConfig[fieldName]?.position) {
                nextConfig[fieldName] = {
                    ...nextConfig[fieldName],
                    position: {
                        ...nextConfig[fieldName].position,
                        ...position,
                    },
                };
            }

            if (options.lockOffsetX !== undefined
                && nextConfig.alignment_locks?.[fieldName]?.enabled) {
                nextConfig.alignment_locks = {
                    ...nextConfig.alignment_locks,
                    [fieldName]: {
                        ...nextConfig.alignment_locks[fieldName],
                        offset_x: Number(options.lockOffsetX) || 0,
                    },
                };
            }

            return nextConfig;
        });
    };

    const handleCustomPreviewSizeChange = ({
        fieldName,
        fontSize,
        fontSizes,
        position,
        positionDelta,
        lockOffsetX,
        skipPreviewRender,
    }) => {
        if (skipPreviewRender) {
            skipNextPreviewRenderRef.current = true;
        }
        commitCustomPreviewConfig((currentConfig) => {
            if (fieldName === "stone_data") {
                const delta = positionDelta || { x: 0, y: 0 };
                const nextStoneData = Object.keys(currentConfig.stone_data || {}).reduce((stoneData, stoneFieldName) => {
                    if (stoneFieldName === "line_space_offset" || stoneFieldName === "font_size") {
                        stoneData[stoneFieldName] = currentConfig.stone_data[stoneFieldName];
                        return stoneData;
                    }
                    const stoneField = currentConfig.stone_data[stoneFieldName];
                    const nextFontSize = Number(fontSizes?.[stoneFieldName]);
                    stoneData[stoneFieldName] = stoneField?.position
                        ? {
                            ...stoneField,
                            ...(Number.isFinite(nextFontSize) && nextFontSize > 0
                                ? { font_size: Math.min(20, Math.max(1, nextFontSize)) }
                                : {}),
                            position: {
                                ...stoneField.position,
                                x: Number(stoneField.position.x) + (Number(delta.x) || 0),
                                y: Number(stoneField.position.y) + (Number(delta.y) || 0),
                            },
                        }
                        : stoneField;
                    return stoneData;
                }, {
                    ...currentConfig.stone_data,
                });
                delete nextStoneData.font_size;
                return {
                    ...currentConfig,
                    stone_data: nextStoneData,
                };
            }
            const isStoneField = STONE_FIELD_NAMES.includes(fieldName);
            const currentField = isStoneField
                ? currentConfig.stone_data?.[fieldName]
                : currentConfig[fieldName];
            if (!currentField?.position) return currentConfig;

            const nextField = {
                ...currentField,
                font_size: Math.min(20, Math.max(1, Number(fontSize) || DEFAULT_TEXT_FONT_SIZE)),
                position: {
                    ...currentField.position,
                    ...(position || {}),
                },
            };
            const nextConfig = {
                ...currentConfig,
                ...(isStoneField
                    ? {
                        stone_data: {
                            ...currentConfig.stone_data,
                            [fieldName]: nextField,
                        },
                    }
                    : { [fieldName]: nextField }),
            };

            if (lockOffsetX !== undefined && nextConfig.alignment_locks?.[fieldName]?.enabled) {
                nextConfig.alignment_locks = {
                    ...nextConfig.alignment_locks,
                    [fieldName]: {
                        ...nextConfig.alignment_locks[fieldName],
                        offset_x: Number(lockOffsetX) || 0,
                    },
                };
            }

            return nextConfig;
        });
    };

    const handleCustomPreviewLockChange = ({ fieldName, enabled, offsetX }) => {
        commitCustomPreviewConfig((currentConfig) => ({
            ...currentConfig,
            alignment_locks: {
                ...(currentConfig.alignment_locks || {}),
                [fieldName]: {
                    ...(currentConfig.alignment_locks?.[fieldName] || {}),
                    enabled,
                    target: "barcode",
                    axis: "center",
                    offset_x: enabled ? (Number(offsetX) || 0) : 0,
                },
            },
        }));
    };

    const handleImageChange = (nextImageConfig, options = {}) => {
        if (options.skipPreviewRender) {
            skipNextPreviewRenderRef.current = true;
        }

        setCheckConfigData?.((currentConfig) => ({
            ...currentConfig,
            config_check_info: {
                ...(currentConfig.config_check_info || {}),
                image_config: nextImageConfig,
            },
        }));
    };

    const handlePreviewLineSpacingChange = (nextSpacing) => {
        const spacing = Math.min(5, Math.max(0.5, parseFloat(nextSpacing) || 0.5));
        setPreviewLineSpacing(spacing);
        commitCustomPreviewConfig((currentConfig) => ({
            ...currentConfig,
            stone_data: {
                ...currentConfig.stone_data,
                line_space_offset: spacing,
            },
        }));
    };

    const handleCustomPreviewSourceAssign = ({ sourceKey, previousSourceKey, position, stoneIndex = 0 }) => {
        commitCustomPreviewConfig((currentConfig) => {
            const nextConfig = {
                ...currentConfig,
                stone_data: { ...currentConfig.stone_data },
            };
            const isStoneSource = Boolean(nextConfig.stone_data?.[sourceKey]?.position);
            const nextPosition = {
                x: parseFloat(position.x),
                y: parseFloat(position.y) - (isStoneSource
                    ? (nextConfig.stone_data.line_space_offset || 2.5) * (stoneIndex + 1)
                    : 0),
            };

            if (isStoneSource) {
                nextConfig.stone_data[sourceKey] = {
                    ...nextConfig.stone_data[sourceKey],
                    position: {
                        ...nextConfig.stone_data[sourceKey].position,
                        ...nextPosition,
                    },
                };
            } else if (nextConfig[sourceKey]?.position) {
                nextConfig[sourceKey] = {
                    ...nextConfig[sourceKey],
                    position: {
                        ...nextConfig[sourceKey].position,
                        ...nextPosition,
                    },
                };
            }

            return nextConfig;
        });

        setPreviewLayerFields((currentFields) => [
            ...currentFields.filter((fieldName) => fieldName !== previousSourceKey),
            ...(currentFields.includes(sourceKey) ? [] : [sourceKey]),
        ]);

        setCheckConfigData?.((currentConfig) => {
            const previousProfileField = previousSourceKey === "stock_id"
                ? "item"
                : previousSourceKey;
            const nextProfileField = sourceKey === "stock_id"
                ? "item"
                : sourceKey;
            const currentFields = currentConfig.config_check_list || [];
            const nextFields = currentFields.filter((fieldName) => fieldName !== previousProfileField);

            if (sourceKey !== "sku" && !nextFields.includes(nextProfileField)) {
                nextFields.push(nextProfileField);
            }

            return {
                ...currentConfig,
                config_check_list: nextFields,
            };
        });
    };

    const handlePreviewStoneCountChange = (nextCount) => {
        skipNextPreviewRenderRef.current = false;
        const count = Math.min(MAX_PREVIEW_STONE_COUNT, Math.max(1, Math.floor(Number(nextCount) || 1)));
        setPreviewStoneCount(count);
        setCheckConfigData?.((currentConfig) => ({
            ...currentConfig,
            config_check_info: {
                ...(currentConfig.config_check_info || {}),
                preview_stone_count: count,
            },
        }));
    };

    const handleStoneListToggle = () => {
        setCheckConfigData?.((currentConfig) => {
            const currentFields = currentConfig.config_check_list || [];
            const currentStoneFields = currentFields.filter((fieldName) => (
                STONE_FIELD_NAMES.includes(fieldName)
            ));

            if (currentStoneFields.length > 0) {
                previousStoneFieldsRef.current = currentStoneFields;
                return {
                    ...currentConfig,
                    config_check_list: currentFields.filter((fieldName) => (
                        !STONE_FIELD_NAMES.includes(fieldName)
                    )),
                };
            }

            const fieldsToRestore = previousStoneFieldsRef.current.length > 0
                ? previousStoneFieldsRef.current
                : STONE_FIELD_NAMES;
            return {
                ...currentConfig,
                config_check_list: [
                    ...currentFields,
                    ...fieldsToRestore.filter((fieldName) => !currentFields.includes(fieldName)),
                ],
            };
        });
    };

    const renderField = (fieldName, content, style = {}) => (
        <div
            key={fieldName}
            className="barcode_profile_preview_field"
            style={{ fontSize: `${9 * zoomScale}px`, ...style }}
            title={FIELD_LABELS[fieldName] || fieldName}
        >
            {content || FIELD_LABELS[fieldName] || fieldName}
        </div>
    );

    const getFieldStyle = (fieldName) => {
        const position = PREVIEW_FIELD_POSITIONS[fieldName] || { x: 0, y: 0 };
        const size = PREVIEW_FIELD_SIZES[fieldName] || { width: 8, height: 1.8 };
        const isStoneField = fieldName.startsWith("stone_")
            || ["shape", "color", "cut", "clarity"].includes(fieldName);
        const topOffset = isStoneField ? 0.75 : PREVIEW_TEXT_OFFSET;

        return {
            left: `${position.x * scale}px`,
            top: `${(position.y + topOffset) * scale}px`,
            width: `${size.width * scale}px`,
            height: `${size.height * scale}px`,
            fontFamily: "Arial, sans-serif",
            fontSize: fieldName === "text"
                ? `${((Number(configInfo.text_font_size) || DEFAULT_TEXT_FONT_SIZE) * 96 / 72) * zoomScale}px`
                : `${8 * zoomScale}px`,
            fontWeight: 700,
            lineHeight: 1,
            whiteSpace: "nowrap",
        };
    };

    const renderConfiguredField = (fieldName) => renderField(
        fieldName,
        getPreviewFieldText(fieldName, configInfo),
        getFieldStyle(fieldName)
    );

    const previewObjects = [
        { fieldName: "sku", active: Boolean(templateValue) },
        { fieldName: "barcode", active: selectedFields.has("barcode") },
        { fieldName: "item", active: selectedFields.has("item") },
        { fieldName: "collection", active: selectedFields.has("collection") },
        { fieldName: "metal", active: selectedFields.has("metal") },
        { fieldName: "price", active: selectedFields.has("price") },
        { fieldName: "weight", active: selectedFields.has("weight") },
        {
            fieldName: "stone_data",
            active: stoneLines.length > 0 && (!selectedFields.has("qr") || templateValue === "template_1"),
        },
    ].filter((field) => field.active);

    const getObjectStyle = (fieldName, position) => {
        const size = PREVIEW_FIELD_SIZES[fieldName] || { width: 8, height: 1.8 };
        const isBarcode = fieldName === "barcode";
        const isTextField = !isBarcode && fieldName !== "stone_data";
        const topOffset = isBarcode
            ? 0
            : fieldName === "stone_data"
                ? 0.75
                : PREVIEW_TEXT_OFFSET;

        return {
            left: `${position.x * scale}px`,
            top: `${(position.y + topOffset) * scale}px`,
            width: isTextField ? "max-content" : `${size.width * scale}px`,
            height: `${size.height * scale}px`,
            overflow: "hidden",
            boxSizing: "border-box",
            fontFamily: "Arial, sans-serif",
            fontSize: `${8 * zoomScale}px`,
            fontWeight: 700,
            lineHeight: 1,
            whiteSpace: "nowrap",
        };
    };

    const getObjectPosition = (fieldName) => dragPositions[fieldName]
        || PREVIEW_FIELD_POSITIONS[fieldName]
        || { x: 0, y: 0 };

    const getObjectContent = (fieldName) => {
        if (fieldName === "barcode") {
            return <span className="barcode_profile_preview_drag_barcode" />;
        }
        if (fieldName === "stone_data") {
            return (
                <span
                    className="barcode_profile_preview_drag_stones"
                    style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: `${8 * zoomScale}px`,
                        fontWeight: 700,
                        lineHeight: 1.35,
                    }}
                >
                    {stoneLines.map((line, index) => (
                        <span key={`stone-line-${index}`}>{line}</span>
                    ))}
                </span>
            );
        }
        return (
            <span
                className="barcode_profile_preview_drag_text"
                style={{
                    display: "block",
                    fontFamily: "Arial, sans-serif",
                    fontSize: `${8 * zoomScale}px`,
                    fontWeight: 700,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                }}
            >
                {getPreviewFieldText(fieldName, configInfo)}
            </span>
        );
    };

    const startDrag = (event, fieldName) => {
        if (event.button !== 0) return;

        const position = getObjectPosition(fieldName);
        const size = PREVIEW_FIELD_SIZES[fieldName] || { width: 8, height: 1.8 };
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        dragRef.current = {
            fieldName,
            startClientX: event.clientX,
            startClientY: event.clientY,
            startPosition: { ...position },
            size,
        };
    };

    const moveDrag = (event) => {
        const drag = dragRef.current;
        if (!drag) return;

        const nextPosition = {
            x: Math.min(
                Math.max(drag.startPosition.x + ((event.clientX - drag.startClientX) / scale), 0),
                LABEL_WIDTH - drag.size.width
            ),
            y: Math.min(
                Math.max(drag.startPosition.y + ((event.clientY - drag.startClientY) / scale), 0),
                (templateValue === "template_1" ? 13 : LABEL_HEIGHT) - drag.size.height
            ),
        };
        drag.position = nextPosition;
        setDragPositions((previous) => ({ ...previous, [drag.fieldName]: nextPosition }));
    };

    const finishDrag = () => {
        const drag = dragRef.current;
        if (!drag) return;

        setSettledFields((previous) => ({ ...previous, [drag.fieldName]: true }));
        dragRef.current = null;
    };

    const renderDragLayer = () => (
        <div className="barcode_profile_preview_drag_layer">
            {previewObjects.map(({ fieldName }) => {
                const position = getObjectPosition(fieldName);
                const originalPosition = PREVIEW_FIELD_POSITIONS[fieldName] || position;
                const showClone = Boolean(dragPositions[fieldName] || settledFields[fieldName]);
                const isDragging = dragRef.current?.fieldName === fieldName;

                return (
                    <React.Fragment key={fieldName}>
                        {showClone && (
                            <div
                                className="barcode_profile_preview_drag_mask"
                                style={getObjectStyle(fieldName, originalPosition)}
                            >
                                {fieldName !== "barcode" && (
                                    <span style={{ visibility: "hidden" }}>
                                        {getObjectContent(fieldName)}
                                    </span>
                                )}
                            </div>
                        )}
                        <div
                            className={`barcode_profile_preview_drag_field${isDragging || settledFields[fieldName] ? " is-dragging" : ""}`}
                            style={getObjectStyle(fieldName, position)}
                            onPointerDown={(event) => startDrag(event, fieldName)}
                            onPointerMove={moveDrag}
                            onPointerUp={finishDrag}
                            onPointerCancel={finishDrag}
                        >
                            {showClone ? getObjectContent(fieldName) : null}
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );

    if (pdfUri && templateValue === "template_1") {
        return (
            <section className="barcode_profile_preview_panel" aria-label="Barcode config preview">
                <CustomTemplatePreviewEditor
                    pdfUri={pdfUri}
                    customTemplateConfig={customPreviewConfig}
                    key={resetKey}
                    activeFields={activePreviewFields}
                    onPositionChange={handleCustomPreviewPositionChange}
                    onSizeChange={handleCustomPreviewSizeChange}
                    onSourceAssign={handleCustomPreviewSourceAssign}
                    onLockChange={handleCustomPreviewLockChange}
                    forceDefaultSku={false}
                    previewText={configInfo.text_message || "Text"}
                    previewTextFontSize={Number(configInfo.text_font_size) || DEFAULT_TEXT_FONT_SIZE}
                    imageConfig={configInfo.image_config}
                    onImageChange={handleImageChange}
                    stoneCount={previewStoneCount}
                    onStoneCountChange={handlePreviewStoneCountChange}
                    stoneListVisible={stoneListVisible}
                    onStoneListToggle={handleStoneListToggle}
                    lineSpacing={previewLineSpacing}
                    onLineSpacingChange={handlePreviewLineSpacingChange}
                />
            </section>
        );
    }

    // Template 3 has its own RFID print layout. The generic editor preview is
    // only meaningful for template 1/2 and otherwise shows misleading fields.
    if (templateValue === "template_3") {
        return null;
    }

    return (
        <section className="barcode_profile_preview_panel" aria-label="Barcode config preview">
            <div className="barcode_profile_preview_editor" ref={previewRef}>
                <div className="barcode_profile_preview_toolbar">
                    <button
                        type="button"
                        className="barcode_profile_preview_zoom_button"
                        onClick={() => changeZoom(-PREVIEW_STEP)}
                        disabled={zoom <= PREVIEW_MIN_ZOOM}
                        aria-label="Zoom out"
                        title="Zoom out"
                    >
                        <FiZoomOut aria-hidden="true" />
                    </button>
                    <span className="barcode_profile_preview_zoom_value">{zoom}%</span>
                    <button
                        type="button"
                        className="barcode_profile_preview_zoom_button"
                        onClick={() => changeZoom(PREVIEW_STEP)}
                        disabled={zoom >= PREVIEW_MAX_ZOOM}
                        aria-label="Zoom in"
                        title="Zoom in"
                    >
                        <FiZoomIn aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="barcode_profile_preview_zoom_button"
                        onClick={() => setZoom(100)}
                        disabled={zoom === 100}
                        aria-label="Reset zoom"
                        title="Reset zoom"
                    >
                        <FiRefreshCw aria-hidden="true" />
                    </button>
                </div>

                {pdfUri && templateValue === "template_1" ? (
                    <div
                        className="barcode_profile_preview_pdf_page"
                        style={{ width: previewWidth, height: previewHeight }}
                    >
                        <Document file={pdfUri} loading="">
                            <Page
                                pageNumber={1}
                                width={previewWidth}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                            />
                        </Document>
                        {renderDragLayer()}
                    </div>
                ) : (
                <div
                    className="barcode_profile_preview_label"
                    style={{ width: previewWidth, height: previewHeight }}
                >
                    {templateValue && renderConfiguredField("sku")}
                    {selectedFields.has("item") && renderConfiguredField("item")}
                    {selectedFields.has("barcode") && (
                        <div
                            className="barcode_profile_preview_field barcode_profile_preview_barcode"
                            title={FIELD_LABELS.barcode}
                            style={getFieldStyle("barcode")}
                        />
                    )}
                    {selectedFields.has("image") && configInfo.image_config?.data && (
                        <BarcodeImagePreviewObject
                            imageConfig={configInfo.image_config}
                            scale={scale}
                            onChange={(nextImageConfig) => handleImageChange(nextImageConfig)}
                        />
                    )}
                    {selectedFields.has("metal") && renderConfiguredField("metal")}
                    {selectedFields.has("weight") && renderConfiguredField("weight")}
                    {selectedFields.has("price") && renderConfiguredField("price")}
                    {selectedFields.has("item_size") && renderConfiguredField("item_size")}
                    {selectedFields.has("text") && renderConfiguredField("text")}
                    {selectedFields.has("qr") && templateValue !== "template_1" && (
                        <div
                            className="barcode_profile_preview_field barcode_profile_preview_qr"
                            title={FIELD_LABELS.qr}
                            style={{
                                left: `${60 * scale}px`,
                                top: `${1.5 * scale}px`,
                                width: `${11 * scale}px`,
                                height: `${11 * scale}px`,
                            }}
                        >
                            <span className="barcode_profile_preview_qr_finder top_left" />
                            <span className="barcode_profile_preview_qr_finder top_right" />
                            <span className="barcode_profile_preview_qr_finder bottom_left" />
                        </div>
                    )}
                    {(!selectedFields.has("qr") || templateValue === "template_1") && stoneLines.map((line, index) => renderField(
                        `stone-line-${index}`,
                        line,
                        {
                            left: `${55 * scale}px`,
                            top: `${(1.2 + (index * 2.2)) * scale}px`,
                            width: `${19 * scale}px`,
                            height: `${1.8 * scale}px`,
                            fontSize: `${8 * zoomScale}px`,
                        }
                    ))}
                    {!templateValue && (
                        <div className="barcode_profile_preview_empty">Select a template</div>
                    )}
                </div>
                )}
            </div>
        </section>
    );
};

export default BarcodeConfigPreview;
